import { useEffect, useState } from "react";
import { Building2, Globe, Link2, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  LAB_SERVICES,
  LAB_SERVICE_LABELS,
  type LabSearchVisibility,
  type LabService,
} from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useFeature } from "@/lib/use-feature";
import { cn } from "@/lib/cn";
import { FieldWithIcon } from "../FieldWithIcon";
import { LogoUploader } from "../LogoUploader";
import { ServicesCheckboxGroup } from "../ServicesCheckboxGroup";
import { AddressFormFields, type AddressFormValues } from "../AddressFormFields";
import { TabSaveBar } from "../TabSaveBar";

type Props = {
  initial: {
    name: string;
    services: LabService[];
    logoUrl: string | null;
    addressSame: boolean;
    address: AddressFormValues;
    searchVisibility: LabSearchVisibility;
  };
  onSaved: () => void;
};

const VISIBILITY_OPTIONS: {
  id: LabSearchVisibility;
  label: string;
  description: string;
  icon: typeof Globe;
}[] = [
  {
    id: "PUBLIC",
    label: "Público",
    description: "Qualquer clínica pode encontrar seu laboratório buscando pelo nome.",
    icon: Globe,
  },
  {
    id: "PARTNERS_ONLY",
    label: "Só por convite",
    description: "Não aparece na busca — conecta apenas com quem receber seu link de convite.",
    icon: Link2,
  },
];

function hasAddress(a: AddressFormValues) {
  return a.zipCode.length === 8 && !!a.address && !!a.city;
}

function sameAddress(a: AddressFormValues, b: AddressFormValues) {
  return (
    a.zipCode === b.zipCode &&
    a.address === b.address &&
    a.addressNumber === b.addressNumber &&
    a.addressComplement === b.addressComplement &&
    a.neighborhood === b.neighborhood &&
    a.city === b.city &&
    a.state === b.state
  );
}

function sameServices<T>(a: T[], b: T[]) {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

export function LabTab({ initial, onSaved }: Props) {
  const hasPublicSearch = useFeature("lab.publicSearch");
  const [name, setName] = useState(initial.name);
  const [services, setServices] = useState<LabService[]>(initial.services);
  const [logo, setLogo] = useState<string | null>(initial.logoUrl);
  const [addrSame, setAddrSame] = useState(initial.addressSame);
  const [addr, setAddr] = useState<AddressFormValues>(initial.address);
  const [searchVisibility, setSearchVisibility] = useState<LabSearchVisibility>(
    initial.searchVisibility,
  );

  useEffect(() => {
    setName(initial.name);
    setServices(initial.services);
    setLogo(initial.logoUrl);
    setAddrSame(initial.addressSame);
    setAddr(initial.address);
    setSearchVisibility(initial.searchVisibility);
  }, [
    initial.name,
    initial.services,
    initial.logoUrl,
    initial.addressSame,
    initial.address,
    initial.searchVisibility,
  ]);

  const update = trpc.organization.updateLabProfile.useMutation({
    onSuccess: () => {
      toast.success("Perfil público atualizado");
      onSaved();
    },
    onError: (err) => toast.error(err.message || "Falha ao salvar"),
  });

  const dirty =
    name !== initial.name ||
    !sameServices(services, initial.services) ||
    logo !== initial.logoUrl ||
    addrSame !== initial.addressSame ||
    !sameAddress(addr, initial.address) ||
    searchVisibility !== initial.searchVisibility;

  const toggleService = (s: LabService) => {
    setServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  };

  return (
    <div className="space-y-6">
      <FieldWithIcon
        icon={Building2}
        label="Nome do laboratório"
        htmlId="pt-lab-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Ex: Lab Dental Premium"
      />

      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Serviços oferecidos
        </p>
        <ServicesCheckboxGroup
          options={LAB_SERVICES}
          labels={LAB_SERVICE_LABELS}
          selected={services}
          onToggle={toggleService}
        />
      </div>

      <LogoUploader value={logo} onChange={setLogo} label="Logo do laboratório" />

      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Visibilidade na busca
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {VISIBILITY_OPTIONS.map((opt) => {
            const active = opt.id === searchVisibility;
            const locked = opt.id === "PUBLIC" && !hasPublicSearch;
            return (
              <button
                key={opt.id}
                type="button"
                disabled={locked}
                onClick={() => setSearchVisibility(opt.id)}
                className={cn(
                  "flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors",
                  locked && "cursor-not-allowed opacity-60",
                  active
                    ? "border-brand-300 bg-brand-50 dark:border-primary/40 dark:bg-primary/10"
                    : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-border dark:bg-card dark:hover:border-border",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                    active
                      ? "bg-brand-100 text-brand-700 dark:bg-primary/20 dark:text-primary"
                      : "bg-zinc-100 text-zinc-500 dark:bg-muted dark:text-muted-foreground",
                  )}
                >
                  {locked ? <Lock className="h-3.5 w-3.5" /> : <opt.icon className="h-3.5 w-3.5" />}
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "block text-sm font-semibold",
                        active
                          ? "text-brand-900 dark:text-primary"
                          : "text-zinc-900 dark:text-foreground",
                      )}
                    >
                      {opt.label}
                    </span>
                    {locked && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
                        Plano pago
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-zinc-500 dark:text-muted-foreground">
                    {opt.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {!hasPublicSearch && (
          <p className="mt-2 text-xs text-zinc-500">
            No plano gratuito seu laboratório não aparece em buscas — pra uma clínica te
            conectar, convide diretamente pelo link em{" "}
            <span className="font-medium text-zinc-700">Cadastros</span>.
          </p>
        )}
      </div>

      <div className="space-y-3">
        <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Endereço
        </p>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50/50 px-3 py-2.5 text-sm">
          <input
            type="checkbox"
            checked={addrSame}
            onChange={(e) => setAddrSame(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-zinc-700">Mesmo endereço da organização (em Dados)</span>
        </label>
        {!addrSame && (
          <AddressFormFields
            values={addr}
            onChange={(p) => setAddr((prev) => ({ ...prev, ...p }))}
          />
        )}
      </div>

      <TabSaveBar
        dirty={dirty}
        isPending={update.isPending}
        onSave={() =>
          update.mutate({
            name: name.trim() || null,
            services,
            logoUrl: logo,
            searchVisibility,
            addressSame: addrSame,
            address:
              !addrSame && hasAddress(addr)
                ? {
                    zipCode: addr.zipCode,
                    address: addr.address,
                    addressNumber: addr.addressNumber || undefined,
                    addressComplement: addr.addressComplement || undefined,
                    neighborhood: addr.neighborhood,
                    city: addr.city,
                    state: addr.state as never,
                  }
                : undefined,
          })
        }
      />
    </div>
  );
}
